/* eslint-disable */
// FilePondSingleUpload.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { LinearProgress } from '@mui/material';
import Alert from 'react-s-alert';

// Import FilePond plugins
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginPdfPreview from 'filepond-plugin-pdf-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

// Register the plugins
registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginFileEncode,
  FilePondPluginFileMetadata,
  FilePondPluginFileValidateSize,
  FilePondPluginImageExifOrientation,
  FilePondPluginPdfPreview,
  FilePondPluginFileValidateType
);

const FilePondSingleUpload = ({
  onChange,
  required,
  value,
  acceptedFileTypes,
  maxFileSize,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleProcessResponse = (response) => {
    if (onChange) {
      onChange(response.data.url);
    }
    setIsUploading(false);
    setUploadProgress(0);
  };

  return (
    <div className="d-flex flex-column mt-3" style={{ width: '100%' }}>
      {isUploading && (
        <div style={{ marginBottom: '10px' }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <div
            style={{ textAlign: 'right', fontSize: '12px', marginTop: '2px' }}
          >
            {Math.round(uploadProgress)}%
          </div>
        </div>
      )}
      <FilePond
        acceptedFileTypes={acceptedFileTypes}
        allowPdfPreview
        pdfPreviewHeight={320}
        pdfComponentExtraParams="toolbar=0&view=fit"
        maxFileSize={maxFileSize}
        labelMaxFileSize={`Maximum file size: ${maxFileSize}`}
        labelMaxFileSizeExceeded="File is too large"
        files={
          value
            ? [
                {
                  source: value,
                  options: {
                    type: 'local',
                  },
                },
              ]
            : null
        }
        className="btn btn-block"
        server={{
          // load: (source, load, error, progress, abort, headers) => {
          //   const myRequest = new Request(value);
          //   fetch(myRequest)
          //     .then((res) => res.blob())
          //     .then(load);
          // },
          process: (
            fieldName,
            file,
            metadata,
            load,
            error,
            progress,
            abort
          ) => {
            // Initialize upload state
            setIsUploading(true);
            setUploadProgress(0);

            // Prepare form data
            const formData = new FormData();
            formData.append('file', file, file.name);

            // Create and configure request
            const request = new XMLHttpRequest();
            request.open('POST', process.env.REACT_APP_EGOV_UPLOAD_URL);
            request.setRequestHeader('X-Api-Key', process.env.REACT_APP_EGOV_UPLOAD_KEY);

            // Track upload progress
            request.upload.onprogress = (e) => {
              if (e.lengthComputable) {
                const progressValue = Math.round((e.loaded / e.total) * 100);
                setUploadProgress(progressValue);
                progress(e.lengthComputable, e.loaded, e.total);
              }
            };

            // Handle response
            request.onload = () => {
              try {
                const response = JSON.parse(request.responseText);

                if (
                  request.status >= 200 &&
                  request.status < 300 &&
                  response.data
                ) {
                  // Success case
                  const fileInfo = {
                    source: response.data.url,
                    options: {
                      type: 'local',
                      file: {
                        name: response.data.file_name,
                        size: response.data.file_size,
                        type: response.data.mime_type,
                      },
                      metadata: {
                        poster: response.data.url,
                      },
                    },
                  };
                  load(fileInfo);
                  handleProcessResponse(response);
                } else {
                  // Server returned error
                  const errorMessage =
                    response.message ||
                    'Upload failed with status: ' + request.status;
                  Alert.error(errorMessage);
                  error(errorMessage);
                  setIsUploading(false);
                  setUploadProgress(0);
                }
              } catch (e) {
                // JSON parsing error
                const errorMessage = 'Invalid server response';
                Alert.error(errorMessage);
                error(errorMessage);
                setIsUploading(false);
                setUploadProgress(0);
              }
            };

            // Handle network errors
            request.onerror = () => {
              const errorMessage = 'Network error occurred during upload';
              Alert.error(errorMessage);
              error(errorMessage);
              setIsUploading(false);
              setUploadProgress(0);
            };

            // Send the request
            request.send(formData);

            // Return abort controller
            return {
              abort: () => {
                request.abort();
                abort();
                setIsUploading(false);
                setUploadProgress(0);
              },
            };
          },
          revert: (uniqueFileId, load, error) => {
            onChange('');
          },
        }}
        name="filepond"
        maxFiles={3}
        labelIdle='<span class="filepond--label-action">Choose file</span>'
        required={required}
        beforeRemoveFile={() => onChange('')}
      />
    </div>
  );
};

FilePondSingleUpload.defaultProps = {
  required: false,
  value: '',
  acceptedFileTypes: ['image/*', 'application/pdf'],
  maxFileSize: '5MB',
};

FilePondSingleUpload.propTypes = {
  onChange: PropTypes.instanceOf(Function).isRequired,
  required: PropTypes.bool,
  value: PropTypes.string,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  maxFileSize: PropTypes.string,
};

export default FilePondSingleUpload;
